/*! © 2026 Corone Laureae. Configurator implementation and visual assets: all rights reserved. */
(() => {
  if (customElements.get('cl-configurator')) return;

  class CoroneLaureaeConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.stage = this.querySelector('[data-clc-stage]');
      this.threeDRoot = this.querySelector('[data-cl3d-root]');
      this.totalNode = this.querySelector('[data-clc-total]');
      this.statusNode = this.querySelector('[data-clc-status]');
      this.submitButton = this.querySelector('[data-clc-submit]');
      this.submitLabel = this.submitButton?.querySelector('span');
      this.submitDefaultLabel = this.submitLabel?.textContent || '';
      this.completionNote = this.querySelector('[data-clc-completion-note]');
      this.deliveryStatus = this.querySelector('[data-clc-delivery-status]');
      this.deliveryNational = this.querySelector('[data-clc-delivery-national]');
      this.deliveryLocal = this.querySelector('[data-clc-delivery-local]');
      this.localConfirm = this.querySelector('[data-clc-local-confirm]');
      this.localConfirmWrap = this.querySelector('[data-clc-local-confirm-wrap]');
      this.resetButton = this.querySelector('[data-clc-reset]');
      this.previewColumn = this.querySelector('[data-clc-preview-column]');
      this.previewOpenButton = this.querySelector('[data-clc-preview-open]');
      this.previewCloseButton = this.querySelector('[data-clc-preview-close]');
      this.mobileBar = this.querySelector('[data-clc-mobile-bar]');
      this.mobileTotalNode = this.querySelector('[data-clc-mobile-total]');
      this.headerElement = document.querySelector('[id$="__header"], sticky-header, .shopify-section-header');
      this.syncPreviewStickyOffset = this.syncPreviewStickyOffset.bind(this);
      window.addEventListener?.('scroll', this.syncPreviewStickyOffset, { passive: true });
      window.addEventListener?.('resize', this.syncPreviewStickyOffset);
      if (this.headerElement && typeof ResizeObserver === 'function') {
        this.headerResizeObserver = new ResizeObserver(this.syncPreviewStickyOffset);
        this.headerResizeObserver.observe(this.headerElement);
      }
      this.ensureTopBabyBreathSlots();
      this.isSubmitting = false;
      this.isPreviewOpen = false;
      this.textInputs = {
        left: this.querySelector('[data-clc-text-left]'),
        right: this.querySelector('[data-clc-text-right]'),
      };
      this.textPreviews = {
        left: this.querySelector('[data-clc-ribbon-text-left]'),
        right: this.querySelector('[data-clc-ribbon-text-right]'),
      };
      this.countNodes = {
        left: this.querySelector('[data-clc-count-left]'),
        right: this.querySelector('[data-clc-count-right]'),
      };
      this.customColorInput = this.querySelector('[data-clc-custom-color]');
      this.customColorWrap = this.querySelector('[data-clc-custom-color-wrap]');
      this.circumferenceInput = this.querySelector('[data-clc-circumference]');
      this.graduationDateInput = this.querySelector('[data-clc-graduation-date]');
      this.detailsInput = this.querySelector('[data-clc-details]');
      this.currency = this.dataset.currency || 'EUR';
      this.cartEnabled = this.dataset.cartEnabled === 'true';
      this.letteringUnitPrice = this.parseMoney(this.dataset.letteringPrice);
      this.defaultLetteringVariantId = this.dataset.letteringVariantId || '';
      this.money = new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: this.currency,
      });
      this.textSegmenter = typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter('it', { granularity: 'grapheme' })
        : null;
      if (this.threeDRoot) {
        this.threeDRoot.cl3dGetState = () => this.threeDState(this.currentState || this.state());
      }
      this.addEventListener('change', (event) => this.handleChange(event));
      this.addEventListener('input', (event) => this.handleInput(event));
      this.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.submitButton?.addEventListener('click', () => this.submit());
      this.resetButton?.addEventListener('click', () => this.resetConfiguration());
      this.previewOpenButton?.addEventListener('click', () => this.openPreview());
      this.previewCloseButton?.addEventListener('click', () => this.closePreview());
      this.previewColumn?.addEventListener('click', (event) => {
        if (this.isPreviewOpen && event.target === this.previewColumn) this.closePreview();
      });

      if (this.mobileBar) {
        this.previewInlineVisible = true;
        this.configuratorVisible = true;
        if ('IntersectionObserver' in window) {
          this.previewObserver = new IntersectionObserver(([entry]) => {
            if (this.isPreviewOpen) return;
            this.previewInlineVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.25);
            this.syncMobileBar();
          }, { threshold: [0, 0.25] });
          this.configuratorObserver = new IntersectionObserver(([entry]) => {
            this.configuratorVisible = Boolean(entry?.isIntersecting);
            this.syncMobileBar();
          });
          this.previewObserver.observe(this.previewColumn);
          this.configuratorObserver.observe(this);
        } else {
          this.mobileBar.hidden = false;
        }
      }

      this.querySelectorAll('[data-clc-addon-card]').forEach((card) => {
        const toggle = card.querySelector('[data-clc-addon]');
        card.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
          field.disabled = !toggle?.checked;
        });
      });

      this.restoreDraft();
      this.update();
      this.syncPreviewStickyOffset();
    }

    ensureTopBabyBreathSlots() {
      const layer = this.querySelector('.clc__slot-layer--baby-breath[data-clc-layer="baby-breath"]');
      if (!layer) return;
      const count = layer.querySelectorAll('.clc__baby-slot').length;
      for (let index = count + 1; index <= 20; index += 1) {
        const piece = document.createElement('span');
        piece.className = `clc__slot-piece clc__baby-slot clc__baby-slot--${index}`;
        layer.append(piece);
      }
    }

    disconnectedCallback() {
      this.previewObserver?.disconnect();
      this.configuratorObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      window.removeEventListener?.('scroll', this.syncPreviewStickyOffset);
      window.removeEventListener?.('resize', this.syncPreviewStickyOffset);
      if (!this.isPreviewOpen) return;
      this.isPreviewOpen = false;
      document.documentElement.classList.remove('clc-preview-modal-open');
      this.restorePreviewBackground();
    }

    syncPreviewStickyOffset() {
      if (!this.previewColumn) return;
      const desktop = window.matchMedia?.('(min-width: 990px)')?.matches;
      if (!desktop) {
        this.style.removeProperty('--clc-sticky-header-offset');
        return;
      }

      const header = this.headerElement
        || document.querySelector('[id$="__header"], sticky-header, .shopify-section-header');
      const bottom = Number(header?.getBoundingClientRect?.().bottom);
      const offset = Number.isFinite(bottom) && bottom > 0 ? Math.ceil(bottom) : 0;
      this.style.setProperty('--clc-sticky-header-offset', `${offset}px`);
    }

    handleChange(event) {
      const target = event.target;

      if (target.matches('[data-clc-addon]')) {
        const card = target.closest('[data-clc-addon-card]');
        card?.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
          field.disabled = !target.checked;
        });
      }

      if (target.matches('[data-clc-local-confirm]') && this.statusNode) {
        this.statusNode.dataset.kind = '';
      }

      this.update();
    }

    handleInput(event) {
      if (event.target.matches('[data-clc-custom-color]')) {
        const value = event.target.value.slice(0, 36);
        if (event.target.value !== value) event.target.value = value;
        event.target.setCustomValidity('');
        this.update();
        return;
      }

      if (event.target.matches('[data-clc-circumference], [data-clc-graduation-date], [data-clc-details]')) {
        event.target.setCustomValidity('');
        this.update();
        return;
      }

      if (!event.target.matches('[data-clc-text-left], [data-clc-text-right]')) return;
      const side = event.target.matches('[data-clc-text-left]') ? 'left' : 'right';
      const value = this.truncateText(event.target.value, 28);
      if (event.target.value !== value) event.target.value = value;
      if (this.countNodes[side]) this.countNodes[side].textContent = String(this.textLength(value));
      if (this.textPreviews[side]) this.textPreviews[side].textContent = value;
      this.update();
    }

    handleKeydown(event) {
      if (!this.isPreviewOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePreview();
        return;
      }

      if (event.key !== 'Tab' || !this.previewColumn) return;
      const focusable = [...this.previewColumn.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((element) => element.tabIndex >= 0 && !element.hidden && element.getClientRects().length > 0);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    syncMobileBar() {
      if (!this.mobileBar) return;
      this.mobileBar.hidden = this.isPreviewOpen || !this.configuratorVisible || this.previewInlineVisible;
    }

    completionRequirements(state) {
      const missing = [];
      if (!state.circumference?.trim()) missing.push('la circonferenza');
      if (!state.graduationDate?.trim()) missing.push('il giorno della laurea');
      if (state.letteringColor === 'Extra' && !state.customLetteringColor?.trim()) {
        missing.push('il colore della scritta Extra');
      }
      if (state.requiresLocalDelivery && !this.localConfirm?.checked) {
        missing.push('la conferma per ritiro o consegna a Latina');
      }
      return missing;
    }

    formatRequirements(requirements) {
      if (requirements.length <= 1) return requirements[0] || '';
      return `${requirements.slice(0, -1).join(', ')} e ${requirements.at(-1)}`;
    }

    checked(selector) {
      return this.querySelector(`${selector}:checked`);
    }

    parseMoney(value) {
      const parsed = Number.parseInt(String(value || '0'), 10);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    formatMoney(cents) {
      return this.money.format(cents / 100);
    }

    textParts(value) {
      const text = String(value || '');
      if (this.textSegmenter) {
        return Array.from(this.textSegmenter.segment(text), (part) => part.segment);
      }
      return Array.from(text);
    }

    truncateText(value, limit) {
      return this.textParts(value).slice(0, limit).join('');
    }

    textLength(value) {
      return this.textParts(value).length;
    }

    contrastFor(hex) {
      const normalized = String(hex || '').replace('#', '').trim();
      if (!/^[0-9a-f]{6}$/i.test(normalized)) return '#fff8e8';
      const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255);
      const linear = channels.map((value) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4));
      const luminance = 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
      return luminance > 0.52 ? '#302b25' : '#fff8e8';
    }

    addonState() {
      return [...this.querySelectorAll('[data-clc-addon]:checked')].map((toggle) => {
        const card = toggle.closest('[data-clc-addon-card]');
        const select = card?.querySelector('[data-clc-addon-variant]');
        const option = select?.selectedOptions?.[0];
        const roseColorSelect = card?.querySelector('[data-clc-rose-color]');
        const roseColorOption = roseColorSelect?.selectedOptions?.[0];
        const bowRoseColorSelect = card?.querySelector('[data-clc-bow-rose-color]');
        const bowRoseColorOption = bowRoseColorSelect?.selectedOptions?.[0];
        const babyColorSelect = card?.querySelector('[data-clc-baby-color]');
        const babyColorOption = babyColorSelect?.selectedOptions?.[0];
        const isBabyBreath = toggle.dataset.layer === 'baby-breath';
        const isRoses = toggle.dataset.layer === 'roses';
        const isBowRoses = toggle.dataset.layer === 'bow-roses';
        const roseCount = option?.dataset.roseCount || '3';
        const variantId = isBabyBreath
          ? babyColorOption?.value || toggle.dataset.variantId || ''
          : isRoses
            ? roseColorOption?.getAttribute(`data-variant-${roseCount}`) || ''
          : option?.value || toggle.dataset.variantId || '';
        const price = this.parseMoney(
          isBabyBreath
            ? babyColorOption?.dataset.price || toggle.dataset.price
            : option?.dataset.price || toggle.dataset.price,
        );
        const variantLabel = option?.textContent?.trim() || '';
        const roseColorLabel = roseColorOption?.textContent?.trim() || '';
        const bowRoseColorLabel = bowRoseColorOption?.textContent?.trim() || '';
        const babyColorLabel = babyColorOption?.textContent?.trim() || '';
        const babyMode = card?.querySelector('[data-clc-baby-mode]')?.value || 'a-ciuffi';
        const babyColorKey = babyColorOption?.dataset.colorKey || '';
        const babyImage = babyColorOption?.dataset.image || '';
        const colorKey = isBowRoses
          ? bowRoseColorOption?.dataset.colorKey || ''
          : roseColorOption?.dataset.colorKey || option?.dataset.colorKey || '';
        const image = isBowRoses
          ? bowRoseColorOption?.dataset.image || ''
          : roseColorOption?.dataset.image || option?.dataset.image || '';
        const color = roseColorOption?.dataset.color
          || babyColorOption?.dataset.color
          || option?.dataset.color
          || toggle.dataset.color
          || '';
        const imageUrl = isBabyBreath ? babyImage : isRoses || isBowRoses ? image : '';
        const mode = option?.dataset.mode || '';
        const level = option?.dataset.level || 'delicata';
        const bowRoseCount = option?.dataset.bowRoseCount || '1';
        const flowerSize = option?.dataset.flowerSize || 'piccola';
        const cartQuantity = Number.parseInt(option?.dataset.cartQuantity || toggle.dataset.cartQuantity || '1', 10) || 1;
        const cleanVariantLabel = variantLabel.replace(/\s*·.*$/, '');
        let detailLabel = cleanVariantLabel === 'Default Title' ? '' : cleanVariantLabel;

        if (toggle.dataset.layer === 'baby-breath') {
          detailLabel = `${babyMode === 'sparsa' ? 'sparsa' : 'a ciuffi'}${babyColorLabel ? ` · ${babyColorLabel}` : ''}`;
        }
        if (toggle.dataset.layer === 'roses') detailLabel = `${roseCount} rose${roseColorLabel ? ` · ${roseColorLabel}` : ''}`;
        if (toggle.dataset.layer === 'bow-roses') detailLabel = `${cleanVariantLabel}${bowRoseColorLabel ? ` · ${bowRoseColorLabel}` : ''}`;
        if (toggle.dataset.layer === 'gardenia') detailLabel = `3 fiori${detailLabel ? ` · ${detailLabel}` : ''}`;
        if (toggle.dataset.layer === 'hydrangea') detailLabel = `16 inserti${detailLabel ? ` · ${detailLabel}` : ''}`;
        if (toggle.dataset.layer === 'lagurus') detailLabel = `24 codine${detailLabel ? ` · ${detailLabel}` : ''}`;

        return {
          layer: toggle.dataset.layer || '',
          title: toggle.dataset.title || 'Accessorio',
          variantId,
          variantLabel,
          detailLabel,
          price,
          color,
          imageUrl,
          babyMode,
          babyColorKey,
          babyImage,
          roseCount,
          colorKey,
          image,
          mode,
          level,
          bowRoseCount,
          flowerSize,
          cartQuantity,
          localOnly: toggle.dataset.localOnly === 'true',
        };
      });
    }

    state() {
      const ribbon = this.checked('[data-clc-ribbon]');
      const bowStyle = this.checked('[data-clc-bow-style]');
      const width = this.checked('[data-clc-width]');
      const bands = this.checked('[data-clc-bands]');
      const lettering = this.checked('[data-clc-lettering-color]');
      const addons = this.addonState();
      const textLeft = this.textInputs.left?.value.trim() || '';
      const textRight = this.textInputs.right?.value.trim() || '';
      const customLetteringColor = this.customColorInput?.value.trim() || '';
      const circumference = this.circumferenceInput?.value.trim() || '';
      const graduationDate = this.graduationDateInput?.value.trim() || '';
      const details = this.detailsInput?.value.trim() || '';
      const basePrice = this.parseMoney(ribbon?.dataset.price);
      const bandsPrice = this.parseMoney(bands?.dataset.price);
      const addonsPrice = addons.reduce((sum, addon) => sum + addon.price, 0);
      const hasLettering = Boolean(textLeft || textRight);
      const letteringPrice = hasLettering ? this.letteringUnitPrice : 0;

      return {
        ribbon: ribbon?.value || '',
        ribbonColor: ribbon?.dataset.swatch || '#b31f2b',
        baseVariantId: ribbon?.dataset.variantId || '',
        basePrice,
        bowStyle: bowStyle?.value || 'farfalla',
        cut: 'punta',
        width: width?.value || 'largo',
        bands: bands?.value || '2',
        bandsVariantId: bands?.dataset.variantId || '',
        bandsPrice,
        addons,
        textLeft,
        textRight,
        letteringColor: lettering?.value || 'Bianco',
        letteringHex: lettering?.dataset.color || '#fffaf0',
        letteringVariantId: lettering?.dataset.variantId || this.defaultLetteringVariantId,
        letteringPrice,
        hasLettering,
        customLetteringColor,
        circumference,
        graduationDate,
        details,
        requiresLocalDelivery: addons.some((addon) => addon.localOnly),
        total: basePrice + bandsPrice + addonsPrice + letteringPrice,
      };
    }

    threeDState(state) {
      const items = state.addons.map((addon) => ({
        id: addon.layer,
        label: `${addon.title}${addon.detailLabel ? ` ${addon.detailLabel}` : ''}`,
        type: addon.layer,
        imageUrl: addon.layer === 'baby-breath' || addon.layer === 'roses' || addon.layer === 'bow-roses'
          ? String(addon.imageUrl || addon.babyImage || addon.image || '').trim()
          : '',
        colorKey: String(
          addon.layer === 'baby-breath'
            ? addon.babyColorKey || addon.colorKey || ''
            : addon.colorKey || addon.babyColorKey || '',
        ).trim(),
        color: /^#[0-9a-f]{6}$/i.test(addon.color || '') ? addon.color : '',
        quantity: Number.parseInt(
          addon.layer === 'roses'
            ? addon.roseCount
            : addon.layer === 'bow-roses'
              ? addon.bowRoseCount
              : addon.cartQuantity || '1',
          10,
        ) || 1,
        mode: addon.layer === 'baby-breath' ? addon.babyMode : addon.mode || '',
        flowerSize: addon.flowerSize || 'piccola',
        localOnly: addon.localOnly,
      }));

      return {
        ribbonColor: state.ribbonColor,
        ribbonColorName: state.ribbon,
        bowShape: state.bowStyle,
        ribbonWidth: state.width,
        bands: Number.parseInt(state.bands, 10) || 2,
        stabilized: items.filter((item) => !item.localOnly),
        fresh: items.filter((item) => item.localOnly),
        textLeft: state.textLeft,
        textRight: state.textRight,
        letteringHex: state.letteringHex,
      };
    }

    update() {
      const state = this.state();
      this.currentState = state;

      this.stage.style.setProperty('--clc-ribbon', state.ribbonColor);
      this.stage.style.setProperty('--clc-ribbon-ink', this.contrastFor(state.ribbonColor));
      this.stage.style.setProperty('--clc-lettering', state.letteringHex);
      const usesCustomLettering = state.letteringColor === 'Extra';
      if (this.customColorWrap) this.customColorWrap.hidden = !usesCustomLettering;
      if (this.customColorInput) {
        this.customColorInput.disabled = !usesCustomLettering;
        if (!usesCustomLettering) this.customColorInput.setCustomValidity('');
      }
      this.stage.dataset.cut = state.cut;
      this.stage.dataset.bowStyle = state.bowStyle;
      this.stage.dataset.width = state.width;
      this.stage.dataset.bands = state.bands;

      [
        'baby-breath',
        'fresh-baby-breath',
        'berries',
        'hypericum',
        'roses',
        'gardenia',
        'hydrangea',
        'lagurus',
        'bow-roses',
      ].forEach((layer) => {
        this.stage.dataset[this.dataKey(`layer-${layer}`)] = String(state.addons.some((addon) => addon.layer === layer));
      });

      const berries = state.addons.find((addon) => addon.layer === 'berries');
      if (berries?.color) this.stage.style.setProperty('--clc-berry', berries.color);
      this.stage.dataset.berriesColor = berries?.variantLabel.toLocaleLowerCase('it-IT').includes('oro') ? 'oro' : 'rosso';

      const babyBreath = state.addons.find((addon) => addon.layer === 'baby-breath');
      this.stage.dataset.babyMode = babyBreath?.babyMode || 'a-ciuffi';
      this.stage.dataset.babyColor = babyBreath?.babyColorKey || 'bianco';
      if (babyBreath?.babyImage) this.stage.style.setProperty('--clc-active-baby-image', `url("${babyBreath.babyImage}")`);

      const babyColorOption = this.querySelector('[data-clc-baby-color]')?.selectedOptions?.[0];
      const babyVisual = this.querySelector('[data-clc-baby-visual]');
      if (babyVisual && babyColorOption?.dataset.image) {
        babyVisual.style.setProperty('--clc-addon-image', `url("${babyColorOption.dataset.image}")`);
      }

      const freshBabyBreath = state.addons.find((addon) => addon.layer === 'fresh-baby-breath');
      this.stage.dataset.freshBabyMode = freshBabyBreath?.mode || 'sparsa';

      const roses = state.addons.find((addon) => addon.layer === 'roses');
      this.stage.dataset.roseCount = roses?.roseCount || '3';
      this.stage.dataset.roseColor = roses?.colorKey || 'rosso';
      if ((roses?.colorKey || 'rosso') === 'rosso') {
        this.stage.style.setProperty('--clc-active-rose-image', 'var(--clc-generated-rose-red-image)');
      } else if (roses?.image) {
        this.stage.style.setProperty('--clc-active-rose-image', `url("${roses.image}")`);
      }

      const roseColorOption = this.querySelector('[data-clc-rose-color]')?.selectedOptions?.[0];
      const roseVisual = this.querySelector('[data-clc-rose-visual]');
      if (roseVisual && roseColorOption?.dataset.image) {
        roseVisual.style.setProperty('--clc-addon-image', `url("${roseColorOption.dataset.image}")`);
      }

      const gardenia = state.addons.find((addon) => addon.layer === 'gardenia');
      this.stage.dataset.gardeniaColor = gardenia?.colorKey || 'rosa-antico';

      const hydrangea = state.addons.find((addon) => addon.layer === 'hydrangea');
      this.stage.dataset.hydrangeaColor = hydrangea?.colorKey || 'bianca';
      const hydrangeaOption = this.querySelector('[data-clc-addon][data-layer="hydrangea"]')
        ?.closest('[data-clc-addon-card]')
        ?.querySelector('[data-clc-addon-variant]')
        ?.selectedOptions?.[0];
      const hydrangeaVisual = this.querySelector('[data-clc-hydrangea-visual]');
      if (hydrangeaVisual && hydrangeaOption?.dataset.image) {
        hydrangeaVisual.style.setProperty('--clc-addon-image', `url("${hydrangeaOption.dataset.image}")`);
      }

      const lagurus = state.addons.find((addon) => addon.layer === 'lagurus');
      this.stage.dataset.lagurusColor = lagurus?.colorKey || 'bianco';
      const lagurusOption = this.querySelector('[data-clc-addon][data-layer="lagurus"]')
        ?.closest('[data-clc-addon-card]')
        ?.querySelector('[data-clc-addon-variant]')
        ?.selectedOptions?.[0];
      const lagurusVisual = this.querySelector('[data-clc-lagurus-visual]');
      if (lagurusVisual && lagurusOption?.dataset.image) {
        lagurusVisual.style.setProperty('--clc-addon-image', `url("${lagurusOption.dataset.image}")`);
      }

      const hypericum = state.addons.find((addon) => addon.layer === 'hypericum');
      this.stage.dataset.hypericumColor = hypericum?.colorKey || 'rosso';

      const bowRoses = state.addons.find((addon) => addon.layer === 'bow-roses');
      this.stage.dataset.bowRoseCount = bowRoses?.bowRoseCount || '1';
      this.stage.dataset.bowRoseSize = bowRoses?.flowerSize || 'piccola';
      this.stage.dataset.bowRoseColor = bowRoses?.colorKey || 'rosso';
      if (bowRoses?.image) this.stage.style.setProperty('--clc-active-bow-rose-image', `url("${bowRoses.image}")`);

      const bowRoseColorOption = this.querySelector('[data-clc-bow-rose-color]')?.selectedOptions?.[0];
      const bowRoseVisual = this.querySelector('[data-clc-bow-rose-visual]');
      if (bowRoseVisual && bowRoseColorOption?.dataset.image) {
        bowRoseVisual.style.setProperty('--clc-addon-image', `url("${bowRoseColorOption.dataset.image}")`);
      }

      if (this.textPreviews.left) this.textPreviews.left.textContent = state.textLeft;
      if (this.textPreviews.right) this.textPreviews.right.textContent = state.textRight;
      if (this.countNodes.left) this.countNodes.left.textContent = String(this.textLength(state.textLeft));
      if (this.countNodes.right) this.countNodes.right.textContent = String(this.textLength(state.textRight));
      if (this.totalNode) this.totalNode.textContent = this.formatMoney(state.total);
      if (this.mobileTotalNode) this.mobileTotalNode.textContent = this.formatMoney(state.total);

      if (this.deliveryStatus) this.deliveryStatus.dataset.mode = state.requiresLocalDelivery ? 'local' : 'national';
      if (this.deliveryNational) this.deliveryNational.hidden = state.requiresLocalDelivery;
      if (this.deliveryLocal) this.deliveryLocal.hidden = !state.requiresLocalDelivery;
      if (this.localConfirmWrap) this.localConfirmWrap.hidden = !state.requiresLocalDelivery;
      if (this.localConfirm) {
        if (!state.requiresLocalDelivery && this.localConfirm.checked) this.localConfirm.checked = false;
        this.localConfirm.disabled = !state.requiresLocalDelivery;
      }
      const completionRequirements = this.completionRequirements(state);
      const completionReady = completionRequirements.length === 0;
      if (this.submitButton) {
        this.submitButton.disabled = this.isSubmitting || !completionReady;
      }
      if (this.submitLabel && !this.isSubmitting) {
        this.submitLabel.textContent = completionReady
          ? this.submitDefaultLabel
          : 'Completa i dati obbligatori';
      }
      if (this.completionNote) {
        this.completionNote.dataset.state = completionReady ? 'ready' : 'incomplete';
        this.completionNote.textContent = completionReady
          ? 'Dati essenziali completi: puoi continuare con il carrello.'
          : `Per continuare, indica ${this.formatRequirements(completionRequirements)}.`;
      }

      const bowStyleLabels = { farfalla: 'farfalla', coccarda: 'coccarda' };
      const widthLabels = { stretto: 'stretto', largo: 'largo' };
      this.querySelector('[data-clc-summary-ribbon]').textContent = state.ribbon || '—';
      this.querySelector('[data-clc-summary-bow]').textContent = `${bowStyleLabels[state.bowStyle]}, ${widthLabels[state.width]}, ${state.bands} bande`;
      this.querySelector('[data-clc-summary-addons]').textContent = state.addons.length
        ? state.addons.map((addon) => addon.detailLabel ? `${addon.title} (${addon.detailLabel})` : addon.title).join(', ')
        : 'Nessuna';
      const textSummary = [];
      if (state.textLeft) textSummary.push(`Sinistra: ${state.textLeft}`);
      if (state.textRight) textSummary.push(`Destra: ${state.textRight}`);
      this.querySelector('[data-clc-summary-text]').textContent = textSummary.join(' · ') || 'Nessuna';
      this.querySelector('[data-clc-summary-lettering]').textContent = state.letteringColor === 'Extra'
        ? state.customLetteringColor
          ? `Extra: ${state.customLetteringColor} (secondo disponibilità)`
          : 'Extra: da indicare (secondo disponibilità)'
        : state.letteringColor;
      this.querySelector('[data-clc-summary-circumference]').textContent = state.circumference || 'Da indicare';
      this.querySelector('[data-clc-summary-graduation-date]').textContent = state.graduationDate || 'Da indicare';

      if (this.statusNode?.dataset.kind !== 'persistent') this.statusNode.textContent = '';
      this.threeDRoot?.dispatchEvent(new CustomEvent('cl3d:update', {
        detail: { state: this.threeDState(state) },
      }));
    }

    dataKey(value) {
      return value.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    }

    draftKey() {
      return `corone-laureae-configurator:${this.dataset.sectionId || 'default'}`;
    }

    saveDraft(state) {
      try {
        const payload = {
          ribbon: state.ribbon,
          bowStyle: state.bowStyle,
          width: state.width,
          bands: state.bands,
          textLeft: state.textLeft,
          textRight: state.textRight,
          letteringColor: state.letteringColor,
          customLetteringColor: state.customLetteringColor,
          circumference: state.circumference,
          graduationDate: state.graduationDate,
          details: state.details,
          addons: state.addons.map((addon) => ({
            layer: addon.layer,
            variantId: addon.variantId,
            babyMode: addon.babyMode,
            babyColorKey: addon.babyColorKey,
            roseCount: addon.roseCount,
            colorKey: addon.colorKey,
            mode: addon.mode,
            level: addon.level,
            bowRoseCount: addon.bowRoseCount,
            flowerSize: addon.flowerSize,
          })),
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(this.draftKey(), JSON.stringify(payload));
        return true;
      } catch (_error) {
        return false;
      }
    }

    restoreDraft() {
      let draft;
      try {
        draft = JSON.parse(localStorage.getItem(this.draftKey()) || 'null');
      } catch (_error) {
        return;
      }
      if (!draft) return;

      const selectRadio = (selector, value) => {
        const input = [...this.querySelectorAll(selector)].find((candidate) => candidate.value === value);
        if (input) input.checked = true;
      };

      selectRadio('[data-clc-ribbon]', draft.ribbon);
      selectRadio('[data-clc-bow-style]', draft.bowStyle || 'farfalla');
      selectRadio('[data-clc-width]', draft.width);
      selectRadio('[data-clc-bands]', draft.bands);
      selectRadio('[data-clc-lettering-color]', draft.letteringColor);

      const savedLeftText = typeof draft.textLeft === 'string' ? draft.textLeft : draft.text;
      if (this.textInputs.left && typeof savedLeftText === 'string') this.textInputs.left.value = this.truncateText(savedLeftText, 28);
      if (this.textInputs.right && typeof draft.textRight === 'string') this.textInputs.right.value = this.truncateText(draft.textRight, 28);
      if (this.customColorInput && typeof draft.customLetteringColor === 'string') {
        this.customColorInput.value = draft.customLetteringColor.slice(0, 36);
      }
      if (this.circumferenceInput && typeof draft.circumference === 'string') {
        this.circumferenceInput.value = draft.circumference.slice(0, 20);
      }
      if (this.graduationDateInput && typeof draft.graduationDate === 'string') {
        this.graduationDateInput.value = draft.graduationDate.slice(0, 80);
      }
      if (this.detailsInput && typeof draft.details === 'string') {
        this.detailsInput.value = draft.details.slice(0, 600);
      }

      if (Array.isArray(draft.addons)) {
        draft.addons.forEach((savedAddon) => {
          const toggle = [...this.querySelectorAll('[data-clc-addon]')].find((candidate) => candidate.dataset.layer === savedAddon.layer);
          if (!toggle) return;
          toggle.checked = true;
          const card = toggle.closest('[data-clc-addon-card]');
          const select = card?.querySelector('[data-clc-addon-variant]');
          if (select) {
            const savedRoseOption = [...select.options].find((option) => option.dataset.roseCount === savedAddon.roseCount);
            const savedColorOption = [...select.options].find((option) => option.dataset.colorKey === savedAddon.colorKey);
            const savedVariantOption = [...select.options].find((option) => option.value === savedAddon.variantId);
            if (savedRoseOption) select.selectedIndex = [...select.options].indexOf(savedRoseOption);
            else if (savedColorOption) select.selectedIndex = [...select.options].indexOf(savedColorOption);
            else if (savedVariantOption) select.value = savedVariantOption.value;
          }

          const roseColorSelect = card?.querySelector('[data-clc-rose-color]');
          if (roseColorSelect && savedAddon.colorKey) {
            const savedColorOption = [...roseColorSelect.options].find((option) => option.dataset.colorKey === savedAddon.colorKey);
            if (savedColorOption) roseColorSelect.value = savedColorOption.value;
          }

          const bowRoseColorSelect = card?.querySelector('[data-clc-bow-rose-color]');
          if (bowRoseColorSelect && savedAddon.colorKey) {
            const savedColorOption = [...bowRoseColorSelect.options].find((option) => option.dataset.colorKey === savedAddon.colorKey);
            if (savedColorOption) bowRoseColorSelect.value = savedColorOption.value;
          }

          const babyColorSelect = card?.querySelector('[data-clc-baby-color]');
          if (babyColorSelect && savedAddon.babyColorKey) {
            const savedColorOption = [...babyColorSelect.options].find((option) => option.dataset.colorKey === savedAddon.babyColorKey);
            if (savedColorOption) babyColorSelect.value = savedColorOption.value;
          }

          const babyModeSelect = card?.querySelector('[data-clc-baby-mode]');
          if (babyModeSelect) {
            const restoredBabyMode = savedAddon.babyMode === 'mista'
              ? 'sparsa'
              : savedAddon.babyMode === 'intervallata'
                ? 'a-ciuffi'
                : savedAddon.babyMode;
            if (['a-ciuffi', 'sparsa'].includes(restoredBabyMode)) babyModeSelect.value = restoredBabyMode;
          }

          card?.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
            field.disabled = false;
          });
        });
      }
    }

    configurationCode() {
      const now = Date.now().toString(36).toUpperCase();
      const random = Math.random().toString(36).slice(2, 6).toUpperCase();
      return `CL-${now}-${random}`;
    }

    cartItems(state, code) {
      const properties = {
        'Codice configurazione': code,
        'Colore nastro': state.ribbon,
        'Stile del fiocco': state.bowStyle,
        'Larghezza nastro': state.width,
        'Bande del nastro': state.bands,
        'Scritta nastro sinistro': state.textLeft || 'Nessuna',
        'Scritta nastro destro': state.textRight || 'Nessuna',
        'Colore scritte': state.letteringColor === 'Extra'
          ? state.customLetteringColor
            ? `Extra: ${state.customLetteringColor} (secondo disponibilità)`
            : 'Extra: da indicare (secondo disponibilità)'
          : state.letteringColor,
        'Decorazioni': state.addons.map((addon) => addon.detailLabel ? `${addon.title}: ${addon.detailLabel}` : addon.title).join(' · ') || 'Nessuna',
        'Circonferenza': state.circumference,
        'Giorno della Laurea': state.graduationDate,
        'Dettagli': state.details || 'Nessun dettaglio aggiuntivo',
      };

      if (state.requiresLocalDelivery) {
        properties['Disponibilità'] = 'Solo Latina — ritiro in negozio o consegna locale';
        properties._solo_latina = 'true';
        properties._conferma_solo_latina = 'sì';
      }

      const items = [{ id: String(state.baseVariantId || ''), quantity: 1, properties }];

      if (state.bands !== '2') {
        items.push({
          id: String(state.bandsVariantId || ''),
          quantity: 1,
          properties: { 'Codice configurazione': code },
        });
      }

      if (state.hasLettering) {
        items.push({
          id: String(state.letteringVariantId || ''),
          quantity: 1,
          properties: {
            'Codice configurazione': code,
            'Scritta nastro sinistro': state.textLeft || 'Nessuna',
            'Scritta nastro destro': state.textRight || 'Nessuna',
            'Colore scritte': properties['Colore scritte'],
          },
        });
      }

      state.addons.forEach((addon) => {
        items.push({
          id: String(addon.variantId || ''),
          quantity: addon.cartQuantity || 1,
          properties: {
            'Codice configurazione': code,
            'Scelta nel configuratore': addon.detailLabel || addon.title,
            ...(addon.localOnly ? { _solo_latina: 'true', _componente_locale_obbligatorio: 'true' } : {}),
          },
        });
      });

      if (items.some((item) => !/^\d+$/.test(item.id))) {
        throw new Error('Una scelta non è più collegata al catalogo. Ricarica la pagina o contattaci.');
      }

      return items;
    }

    async submit() {
      const state = this.state();

      if (!this.validateConfiguration(state)) return;

      if (state.requiresLocalDelivery && !this.localConfirm?.checked) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = 'Conferma la disponibilità solo a Latina prima di continuare.';
        this.localConfirm?.focus();
        return;
      }

      const code = this.configurationCode();
      this.saveDraft(state);

      if (!this.cartEnabled) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = `Prova salvata su questo dispositivo · ${code}`;
        return;
      }

      if (!state.baseVariantId) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = 'Non riesco a collegare la variante della corona. Riprova o contattaci.';
        return;
      }

      const originalLabel = this.submitButton.querySelector('span')?.textContent || '';
      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.submitButton.querySelector('span').textContent = 'Aggiungo al carrello…';
      this.statusNode.dataset.kind = '';
      this.statusNode.textContent = '';

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.cartItems(state, code) }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Impossibile aggiungere la configurazione');
        }

        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = 'Configurazione aggiunta. Ti porto al carrello…';
        window.setTimeout(() => window.location.assign(`${root}cart`), 500);
      } catch (error) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = error.message || 'Si è verificato un errore. Riprova.';
        this.isSubmitting = false;
        this.submitButton.querySelector('span').textContent = originalLabel;
        this.update();
      }
    }

    validateConfiguration(state) {
      const requiredFields = [
        {
          input: this.circumferenceInput,
          value: state.circumference,
          message: 'Indica la circonferenza della testa.',
        },
        {
          input: this.graduationDateInput,
          value: state.graduationDate,
          message: 'Indica il giorno della laurea o un periodo di riferimento.',
        },
      ];

      if (state.letteringColor === 'Extra') {
        requiredFields.push({
          input: this.customColorInput,
          value: state.customLetteringColor,
          message: 'Indica il colore desiderato per la scritta Extra.',
        });
      }

      requiredFields.forEach(({ input, value, message }) => {
        input?.setCustomValidity(value ? '' : message);
      });

      const invalid = requiredFields.find(({ input }) => input && !input.checkValidity());
      if (invalid) {
        if (this.statusNode) {
          this.statusNode.dataset.kind = 'validation';
          this.statusNode.textContent = invalid.message;
        }
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        invalid.input.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        invalid.input.focus({ preventScroll: true });
        invalid.input.reportValidity();
        return false;
      }

      const numericId = (value) => /^\d+$/.test(String(value || ''));
      const invalidAddon = state.addons?.find((addon) => !numericId(addon.variantId));
      const hasInvalidCatalogLink = !numericId(state.baseVariantId)
        || (state.bands !== '2' && !numericId(state.bandsVariantId))
        || (state.hasLettering && !numericId(state.letteringVariantId))
        || Boolean(invalidAddon);

      if (hasInvalidCatalogLink) {
        if (this.statusNode) {
          this.statusNode.dataset.kind = 'validation';
          this.statusNode.textContent = 'Una scelta non è più disponibile nel catalogo. Ricarica la pagina o contattaci.';
        }
        const invalidControl = invalidAddon
          ? this.querySelector(`[data-clc-addon][data-layer="${invalidAddon.layer}"]`)
          : this.querySelector('[data-clc-ribbon]:checked');
        invalidControl?.focus();
        return false;
      }

      return true;
    }

    resetConfiguration() {
      if (this.isSubmitting) return;

      const changedChoice = [...this.querySelectorAll('input[type="radio"], input[type="checkbox"]')]
        .some((input) => input.checked !== input.defaultChecked);
      const changedSelect = [...this.querySelectorAll('select')].some((select) => {
        const defaultIndex = [...select.options].findIndex((option) => option.defaultSelected);
        return select.selectedIndex !== (defaultIndex >= 0 ? defaultIndex : 0);
      });
      const hasEnteredText = [
        this.textInputs.left,
        this.textInputs.right,
        this.customColorInput,
        this.circumferenceInput,
        this.graduationDateInput,
        this.detailsInput,
      ].some((input) => Boolean(input?.value.trim()));

      if ((changedChoice || changedSelect || hasEnteredText)
        && !window.confirm('Vuoi azzerare tutte le scelte e ricominciare?')) return;

      try {
        localStorage.removeItem(this.draftKey());
      } catch (_error) {
        // Il ripristino dei controlli funziona anche se lo storage non è disponibile.
      }

      this.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        input.checked = input.defaultChecked;
        input.setCustomValidity('');
      });
      this.querySelectorAll('select').forEach((select) => {
        const defaultIndex = [...select.options].findIndex((option) => option.defaultSelected);
        select.selectedIndex = defaultIndex >= 0 ? defaultIndex : 0;
      });
      [
        this.textInputs.left,
        this.textInputs.right,
        this.customColorInput,
        this.circumferenceInput,
        this.graduationDateInput,
        this.detailsInput,
      ].forEach((input) => {
        if (!input) return;
        input.value = '';
        input.setCustomValidity('');
      });
      this.querySelectorAll('[data-clc-addon-card]').forEach((card) => {
        const toggle = card.querySelector('[data-clc-addon]');
        card.querySelectorAll('[data-clc-dependent], [data-clc-addon-variant]').forEach((field) => {
          field.disabled = !toggle?.checked;
        });
      });

      this.update();
      if (this.statusNode) {
        this.statusNode.dataset.kind = 'persistent';
        this.statusNode.textContent = 'Configurazione azzerata. Puoi ricominciare dal colore del nastro.';
      }
      this.querySelector('[data-clc-ribbon]')?.focus();
    }

    setPreviewBackgroundInert() {
      this.previewBackgroundState = [];
      let activeBranch = this.previewColumn;

      while (activeBranch && activeBranch !== document.body) {
        const parent = activeBranch.parentElement;
        if (!parent) break;

        [...parent.children].forEach((sibling) => {
          if (sibling === activeBranch || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
          this.previewBackgroundState.push({
            element: sibling,
            inert: Boolean(sibling.inert),
          });
          sibling.inert = true;
        });

        activeBranch = parent;
      }
    }

    restorePreviewBackground() {
      (this.previewBackgroundState || []).forEach(({ element, inert }) => {
        if (!element?.isConnected) return;
        element.inert = inert;
      });
      this.previewBackgroundState = [];
    }

    openPreview() {
      if (!this.previewColumn || this.isPreviewOpen || !window.matchMedia('(max-width: 749px)').matches) return;
      this.isPreviewOpen = true;
      this.previewPreviouslyFocused = document.activeElement;
      this.previewScrollY = window.scrollY;
      this.previewColumn.classList.add('is-mobile-open');
      this.previewColumn.setAttribute('role', 'dialog');
      this.previewColumn.setAttribute('aria-modal', 'true');
      this.previewOpenButton?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('clc-preview-modal-open');
      this.previewCloseButton?.focus({ preventScroll: true });
      this.setPreviewBackgroundInert();
      this.syncMobileBar();
      window.requestAnimationFrame(() => this.previewCloseButton?.focus({ preventScroll: true }));
    }

    closePreview() {
      if (!this.isPreviewOpen || !this.previewColumn) return;
      this.isPreviewOpen = false;
      this.previewColumn.classList.remove('is-mobile-open');
      this.previewColumn.removeAttribute('role');
      this.previewColumn.removeAttribute('aria-modal');
      this.previewOpenButton?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('clc-preview-modal-open');
      this.restorePreviewBackground();
      this.syncMobileBar();
      const restoreTarget = this.previewPreviouslyFocused?.isConnected
        ? this.previewPreviouslyFocused
        : this.previewOpenButton;
      const scrollY = this.previewScrollY;
      window.requestAnimationFrame(() => {
        if (Number.isFinite(scrollY)) window.scrollTo(0, scrollY);
        restoreTarget?.focus({ preventScroll: true });
      });
    }
  }

  customElements.define('cl-configurator', CoroneLaureaeConfigurator);
})();
